import React, { useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import CohortPrefixSelector from './CohortPrefixSelector';
import Spinner from './spinner/Spinner';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

function AssignCohortPrefix({ cohortId, currentPrefixId, toggleModal }) {
  const [prefixId, setPrefixId] = useState(currentPrefixId || '');
  const [cohortRound, setCohortRound] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data) => axiosInstancev2.post(`/cohorts/${cohortId}/assign-prefix`, data),
    {
      onSuccess: () => {
        toast.success('Student ID prefix assigned successfully');
        queryClient.invalidateQueries(['cohort', cohortId]);
        queryClient.invalidateQueries(['cohorts']);
        toggleModal();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || 'Failed to assign prefix');
        setIsSubmitting(false);
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!prefixId) {
      toast.error('Please select a prefix');
      return;
    }
    setIsSubmitting(true);
    mutation.mutate({
      prefix_id: prefixId,
      cohort_round: cohortRound,
    });
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-blue-800 text-sm">
            Assigning a Student ID prefix enables automatic student number
            generation for new students in this cohort. Format:
            PLP-XXX-YYMM-SSSSS
          </p>
        </div>

        <div className="flex flex-col space-y-1">
          <label htmlFor="prefix" className="text-base font-medium">
            Student ID Prefix
          </label>
          <CohortPrefixSelector value={prefixId} onChange={setPrefixId} />
        </div>

        <div className="flex flex-col space-y-1">
          <label htmlFor="cohortRound" className="text-base font-medium">
            Cohort Round (for partner cohorts)
          </label>
          <input
            type="number"
            id="cohortRound"
            value={cohortRound}
            onChange={(e) => setCohortRound(parseInt(e.target.value, 10) || 1)}
            min="1"
            max="99"
            className="border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-persian-500"
          />
          <p className="text-gray-500 text-xs mt-1">
            Used as period for partner/accelerator cohorts (e.g., Round 1, Round
            2)
          </p>
        </div>

        {isSubmitting ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={toggleModal}
              className="border border-claret-500 px-4 py-2 rounded-md text-claret-500 text-sm flex items-center space-x-2"
            >
              <CancelIcon fontSize="inherit" />
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              className="bg-claret-500 px-4 py-2 rounded-md text-white text-sm flex items-center space-x-2"
            >
              <SaveIcon fontSize="inherit" />
              <span>Assign Prefix</span>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default AssignCohortPrefix;
